import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { cn } from '../../utils/cn';

// Importation des images
import imgMakara from '../../assets/img/img-makara.png';
import imgMLaVie from '../../assets/img/img-mLaVie.png';
import imgLdmo from '../../assets/img/img-ldmo.png';
import imgTalkinObout from '../../assets/img/img-talkinObout.png';

// Composant ProjectCard avec les animations
const ProjectCard = ({ project }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      className="group relative h-[400px] w-full overflow-hidden rounded-lg bg-zinc-100 dark:bg-zinc-800 hover:shadow-xl transition-all duration-500"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      variants={{
        hidden: { opacity: 0, y: 20 },
        visible: {
          opacity: 1,
          y: 0,
          transition: { duration: 0.6 }
        }
      }}
    >
      {/* Carte avec image en arrière-plan */}
      <div
        className="absolute inset-0 z-0 transition-transform duration-500 group-hover:scale-110"
        style={{
          backgroundImage: `url(${project.image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />

      {/* Overlay foncé au survol */}
      <div
        className={`absolute inset-0 bg-black transition-opacity duration-500 ${
          isHovered ? 'opacity-70' : 'opacity-0'
        }`}
      />

      {/* Contenu de la carte */}
      <div className="absolute inset-0 z-10 flex flex-col justify-between p-6 text-white">
        <div>
          {/* Tags en haut */}
          <div className="flex flex-wrap gap-2 mb-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500 transform translate-y-2 group-hover:translate-y-0">
            {project.tags.map((tag, index) => (
              <span
                key={index}
                className="rounded-full bg-white/20 px-3 py-1 text-xs backdrop-blur-sm"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Titre et description en bas */}
        <div className={cn(
          "transform transition-all duration-700",
          isHovered
            ? "translate-y-0 opacity-100"
            : "translate-y-8 opacity-0"
        )}>
          <h3 className="text-2xl font-bold mb-2">{project.title}</h3>
          <p className="text-sm text-white/90 mb-4">{project.description}</p>

          {/* Boutons */}
          <div className="flex space-x-3">
            {project.showGithub && (
              <a
                href={project.githubLink}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-md bg-zinc-800 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-zinc-700 focus:outline-none"
              >
                GitHub
              </a>
            )}
            <a
              href={project.demoLink}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-blue-500 focus:outline-none"
            >
              {project.demoText || "Live Demo"}
            </a>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const Projects = () => {
  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  const stagger = {
    visible: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  // Données des projets
  const projects = [
    {
      id: 1,
      title: "Institut La Liberté d'Être",
      description: "Site vitrine pour un institut de soins, épilation et bien être. L'institut La Liberté d'Être offre des soins sur-mesure alliant expertise, détente et produits naturels de qualité. Nouveauté : épilation définitive au laser et bientôt, une boutique en ligne de produits de beauté et bien-être sélectionnés pour leur qualité, éthique et respect de l'environnement. *Boutique en cours de réalisation",
      image: imgMLaVie,
      tags: ["HTML", "CSS", "JavaScript", "PHP", "TailwindCSS"],
      demoLink: "https://institut-lalibertedetre.fr/",
      demoText: "Site",
      showGithub: false
    },
    {
      id: 2,
      title: "Makara",
      description: "Projet en cours de création. J'ai eu la chance d'intégrer l'équipe de Makara Média en stage juste après le passage de mon titre professionnel. Ce projet a pour but de faciliter la communication des entrepreuneurs et des particuliers sur les réseaux sociaux, avec l'aide de l'intelligence artificielle s'il le souhaite.",
      image: imgMakara,
      tags: ["TypeScript", "React", "Symfony", "TailwindCSS", "AWS"],
      demoLink: "",
      demoText: "Site bientôt disponible",
      showGithub: false
    },
    {
      id: 3,
      title: "Talkin O'Bout",
      description: "Projet de fin de formation. Nous avons créé un site regroupant des articles classés par thèmes où les utilisateurs peuvent interagir entre eux et avec le contenu du site.",
      image: imgTalkinObout,
      tags: ["TypeScript", "React", "Symfony", "TailwindCSS", "MySQL"],
      githubLink: "https://github.com/yourusername/snake-game",
      demoLink: "https://talkinobout.com",
      demoText: "Site",
      showGithub: false
    }
  ];

  return (
    <div className="bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-white">
        {/* Header */}
        <motion.div
          className="text-center mb-12"
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
        >
          <h1 className="text-4xl font-bold mb-2 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-500">
            Mes Projets
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Découvrez une sélection de mes projets récents. Chaque projet représente différentes compétences
            et technologies que j'ai utilisées pour résoudre des problèmes spécifiques.
          </p>
        </motion.div>

        {/* Featured Project - Full width */}
        <motion.div
          className="bg-gray-800 rounded-2xl p-6 shadow-lg mb-8 relative overflow-hidden group"
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
        >
          <div className="absolute -top-20 -right-20 w-64 h-64 bg-blue-500 opacity-10 rounded-full blur-3xl group-hover:opacity-20 transition-opacity duration-500"></div>
          <div className="relative flex flex-col md:flex-row md:items-center gap-6">
            <div className="md:w-1/2">
              <h2 className="text-2xl font-bold mb-2">Projet en vedette</h2>
              <h3 className="text-xl text-blue-400 mb-4">LDMO</h3>
              <p className="text-gray-300 mb-4">
                Site vitrine pour un maître d'œuvre expérimenté et professionnel. LDMO - Maîtrise d'œuvre accompagne depuis 25 ans particuliers, investisseurs et professionnels dans leurs projets de construction, rénovation et extension. Basé entre Dombes et Beaujolais, Laurent Desplanques propose un service personnalisé avec un interlocuteur unique par chantier. Spécialisé dans l'accompagnement complet de la conception à la livraison, LDMO garantit transparence, respect du budget et du planning grâce à son réseau d'artisans locaux fiables et son expertise technique éprouvée.
              </p>
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="text-xs px-2 py-1 rounded-full bg-gray-700 text-blue-300">HTML</span>
                <span className="text-xs px-2 py-1 rounded-full bg-gray-700 text-blue-300">CSS</span>
                <span className="text-xs px-2 py-1 rounded-full bg-gray-700 text-blue-300">JavaScript</span>
                <span className="text-xs px-2 py-1 rounded-full bg-gray-700 text-blue-300">PHP</span>
                <span className="text-xs px-2 py-1 rounded-full bg-gray-700 text-blue-300">TailwindCSS</span>
              </div>
              <div className="flex space-x-4">
                <a
                  href="https://ldmo.vercel.app/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white py-2 px-4 rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-300"
                >
                  <FaExternalLinkAlt /> Site en cours d'achèvement
                </a>
              </div>
            </div>
            <div className="md:w-1/2 mt-6 md:mt-0">
              <div className="rounded-xl overflow-hidden shadow-lg">
                <img
                  src={imgLdmo}
                  alt="M La Vie - Institut de soins"
                  className="w-full object-cover transform transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Projects Grid */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          variants={stagger}
          initial="hidden"
          animate="visible"
        >
          {projects.map(project => (
            <ProjectCard key={project.id} project={project} />
          ))}
        </motion.div>

        {/* Call To Action */}
        <motion.div
          className="bg-gray-800 rounded-2xl p-6 shadow-lg mt-8 text-center"
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
        >
          <h3 className="text-xl font-bold mb-2">Vous avez un projet en tête?</h3>
          <p className="text-gray-400 mb-6">
            Je suis toujours intéressé par de nouveaux projets et collaborations.
            N'hésitez pas à me contacter pour en discuter.
          </p>
          <a
            href="/contact"
            className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 px-6 rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-300"
          >
            Me contacter
          </a>
        </motion.div>
      </div>
    </div>
  );
};

export default Projects;
